import { CacheControl } from '@tusbar/cache-control'
import React from 'react'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { reportClientError } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { NotFoundPage } from '../../../app/_not-found.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { SizeChartNav } from '../../../brunswick/components/size-chart/SizeChartNav'
import { SizeChartTable } from '../../../brunswick/components/size-chart/SizeChartTable'
import { SeoMeta } from '../../../components/SeoMeta'
import { cachePolicies } from '../../../lib/response-cache'
import { singleQueryParamValue } from '../../../lib/utils'
import { SizeChartTable as ISizeChartTable, SizeChart } from '../../../types/graphql'
import { MagnoliaPage, MagnoliaPageContext, MagnoliaPageInitialProps } from '../MagnoliaApp'

import { FETCH_SIZE_CHARTS } from './queries'
import { AltText, Body, BreakLine, Header, NavWrap, TableWrap } from './styles'
import { useSizeChart } from './useSizeChart'

type SizeChartsPageProps = MagnoliaPageInitialProps<{
  errors: ReadonlyArray<Error>
  loading: boolean
  mensSizeCharts: SizeChart[]
  womensSizeCharts: SizeChart[]
}>

export const SizeChartsPage: MagnoliaPage<SizeChartsPageProps> = ({
  errors: errors,
  loading: loading,
  mensSizeCharts: mensSizeCharts,
  womensSizeCharts: womensSizeCharts,
}) => {
  const {
    sizesSection: { noChart, pageTitle },
  } = useTranslation()
  const hasErrors = errors.length > 0
  const didQueryFail: boolean =
    hasErrors || (!loading && mensSizeCharts === null) || (!loading && womensSizeCharts === null)

  // Derive the current selected chart from query parameters
  const { selectedGender, selectedChart, setSelectedChart } = useSizeChart({
    mens: mensSizeCharts,
    womens: womensSizeCharts,
  })

  React.useEffect(() => {
    if (!didQueryFail || !hasErrors) return
    reportClientError({
      error: errors?.[0],
      context: {
        scope: 'Size Chart Page',
        label: 'siteFixtures query failed',
      },
    })
  }, [didQueryFail, errors, hasErrors])

  if (didQueryFail) return <NotFoundPage />

  return (
    <>
      <SeoMeta title={pageTitle} noindex />

      <Header asTag='h1'>{pageTitle}</Header>
      <Body>
        <NavWrap>
          {selectedChart && (
            <SizeChartNav
              mensSizeCharts={mensSizeCharts}
              womensSizeCharts={womensSizeCharts}
              selectedGender={selectedGender}
              selectedChart={selectedChart}
              onChartSelected={setSelectedChart}
            />
          )}
        </NavWrap>
        <TableWrap>
          {selectedChart?.tables?.length ? (
            selectedChart.tables.map((table: ISizeChartTable, index: number) => (
              <div key={index}>
                <SizeChartTable {...table} />
                {selectedChart.tables && index !== selectedChart.tables.length - 1 ? (
                  <BreakLine />
                ) : null}
              </div>
            ))
          ) : (
            <AltText>{noChart}</AltText>
          )}
        </TableWrap>
      </Body>
    </>
  )
}

SizeChartsPage.getInitialProps = async function (pageContext: MagnoliaPageContext) {
  const client = pageContext.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const preview: boolean = pageContext.query['content'] === 'preview'
  const cacheBypass =
    singleQueryParamValue(pageContext.query.features, '') === process.env.CACHE_BYPASS_FEATURE
  const explicitNoCache = preview || cacheBypass

  // @TODO: remove `any` generics for full type-safety
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, errors, loading } = await client.query<any, any>({
    query: FETCH_SIZE_CHARTS,
    variables: { preview },
  })

  const graphQLErrors: ReadonlyArray<Error> = errors || []
  const mensSizeCharts: SizeChart[] = data.siteFixtures.mensSizeCharts
  const womensSizeCharts: SizeChart[] = data.siteFixtures.womensSizeCharts

  return {
    errors: graphQLErrors,
    loading,
    mensSizeCharts,
    responseCacheControl: cachePolicy(graphQLErrors, explicitNoCache),
    womensSizeCharts,
  }
}

function cachePolicy(errors: ReadonlyArray<Error>, preview: boolean): CacheControl {
  return preview || errors.length > 0 ? cachePolicies.noCache : cachePolicies.publicHtmlPolicy
}
